import React, { useEffect, useMemo, useState } from 'react';
import { Box, Center, Flex, Spinner, Text } from '@chakra-ui/react';
import { Button } from 'src/components/atoms/Button';
import { Icon } from 'src/components/atoms/Icon';
import { CustomToast } from 'src/components/atoms/CustomToast';
import { useCustomToast } from 'src/hooks/useCustomToast';
import { useAppContext } from 'src/contexts/AppContext';
import { useCampaignMappingsQuery } from 'src/api/campaignMappings';
import {
  CampaignGroup as CampaignGroupApi,
  useCampaignGroupsQuery,
  useCreateCampaignGroupMutation,
  useDeleteCampaignGroupMutation,
  useUpdateCampaignGroupMutation,
} from 'src/api/campaignGroups';
import { dayjs } from 'src/libs/dayjs';
import {
  CampaignGroupsTable,
  CampaignGroup as CampaignGroupsTableRow,
} from '../../components/molecules/CampaignGroupsTable';
import { ViewGroupDrawer } from '../../components/molecules/ViewGroupDrawer';
import { CreateGroupDrawer } from '../../components/molecules/CreateGroupDrawer';
import { DeleteGroupDialog } from '../../components/molecules/DeleteGroupDialog';
import { Campaign } from '../../components/molecules/CampaignList';

type SelectedCampaignGroup = CampaignGroupsTableRow & {
  campaignList: Campaign[];
};

const mapApiCampaignToUI = (campaign?: {
  id: string;
  name: string;
}): Campaign | null => {
  if (!campaign) {
    return null;
  }
  return {
    id: String(campaign.id),
    name: campaign.name,
  };
};

const mapApiGroupToSelected = (
  group: CampaignGroupApi,
): SelectedCampaignGroup => {
  const campaignList = group.campaignList.map((campaign) =>
    mapApiCampaignToUI(campaign),
  );

  const validCampaigns = campaignList.filter(
    (campaign): campaign is Campaign => campaign !== null,
  );

  return {
    id: group.id,
    groupName: group.groupName,
    campaigns: validCampaigns.length,
    created: group.created ? dayjs(group.created).format('MMM DD, YYYY') : '',
    campaignList: validCampaigns,
  };
};

export const CampaignGroupsTab: React.FC = () => {
  const { currentCustomer } = useAppContext();
  const customerId = currentCustomer?.customerName ?? '';

  const { toastState, showSuccess, showError, hideToast } = useCustomToast();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'view' | 'edit'>('view');
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null);
  const [groupName, setGroupName] = useState('');
  const [editGroupName, setEditGroupName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCampaigns, setSelectedCampaigns] = useState<Set<string>>(
    new Set(),
  );
  const [editGroupCampaigns, setEditGroupCampaigns] = useState<Campaign[]>([]);
  const [isAddingCampaigns, setIsAddingCampaigns] = useState(false);
  const [addCampaignsSearchQuery, setAddCampaignsSearchQuery] = useState('');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const {
    data: campaignGroupsData = [],
    isLoading: isCampaignGroupsLoading,
    isError: isCampaignGroupsError,
  } = useCampaignGroupsQuery(customerId);

  const { data: campaignMappingsData = [] } =
    useCampaignMappingsQuery(customerId);

  const createCampaignGroup = useCreateCampaignGroupMutation(customerId);
  const updateCampaignGroup = useUpdateCampaignGroupMutation(customerId);
  const deleteCampaignGroup = useDeleteCampaignGroupMutation(customerId);

  const allCampaigns = useMemo<Campaign[]>(() => {
    const campaignsMap = new Map<string, string>();

    campaignMappingsData.forEach((mapping) => {
      campaignsMap.set(String(mapping.campaign_id), mapping.campaign_name);
    });

    campaignGroupsData.forEach((group) => {
      group.campaignList.forEach((campaign) => {
        campaignsMap.set(String(campaign.id), campaign.name);
      });
    });

    return Array.from(campaignsMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [campaignGroupsData, campaignMappingsData]);

  const tableGroups = useMemo<CampaignGroupsTableRow[]>(() => {
    return campaignGroupsData.map((group) => ({
      id: group.id,
      groupName: group.groupName,
      campaigns: group.campaignList.length,
      created: group.created ? dayjs(group.created).format('MMM DD, YYYY') : '',
    }));
  }, [campaignGroupsData]);

  const activeGroup = useMemo<SelectedCampaignGroup | null>(() => {
    if (!activeGroupId) {
      return null;
    }
    const group = campaignGroupsData.find((item) => item.id === activeGroupId);
    if (!group) {
      return null;
    }
    return mapApiGroupToSelected(group);
  }, [activeGroupId, campaignGroupsData]);

  useEffect(() => {
    if (drawerMode === 'edit' && activeGroup) {
      setEditGroupName(activeGroup.groupName);
      setEditGroupCampaigns(activeGroup.campaignList);
    }
  }, [drawerMode, activeGroup]);

  const resetDrawerState = () => {
    setActiveGroupId(null);
    setDrawerMode('view');
    setEditGroupName('');
    setEditGroupCampaigns([]);
    setIsAddingCampaigns(false);
    setAddCampaignsSearchQuery('');
  };

  const handleGroupClick = (group: CampaignGroupsTableRow) => {
    setActiveGroupId(group.id);
    setDrawerMode('view');
    setIsDrawerOpen(true);
    setIsAddingCampaigns(false);
    setAddCampaignsSearchQuery('');
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    resetDrawerState();
  };

  const handleOpenCreateDrawer = () => {
    if (!customerId) {
      return;
    }
    setIsCreateDrawerOpen(true);
  };

  const handleCloseCreateDrawer = () => {
    setIsCreateDrawerOpen(false);
    setGroupName('');
    setSearchQuery('');
    setSelectedCampaigns(new Set());
  };

  const handleCreateGroup = async () => {
    if (
      !customerId ||
      !groupName.trim() ||
      selectedCampaigns.size === 0 ||
      createCampaignGroup.isLoading
    ) {
      return;
    }

    const campaignIds = Array.from(selectedCampaigns)
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));

    if (campaignIds.length === 0) {
      showError(
        'Unable to create group',
        'Select at least one valid campaign before creating a group.',
      );
      return;
    }

    try {
      await createCampaignGroup.mutateAsync({
        groupName: groupName.trim(),
        campaignIds,
      });
      showSuccess(
        'Campaign group created',
        'The campaign group has been saved.',
      );
      handleCloseCreateDrawer();
    } catch (error) {
      showError(
        'Unable to create group',
        'Something went wrong while saving the campaign group.',
      );
    }
  };

  const handleEditGroupFromView = () => {
    if (!activeGroup) {
      return;
    }
    setEditGroupName(activeGroup.groupName);
    setEditGroupCampaigns(activeGroup.campaignList);
    setIsAddingCampaigns(false);
    setDrawerMode('edit');
  };

  const handleManageClick = (group: CampaignGroupsTableRow) => {
    setActiveGroupId(group.id);
    setDrawerMode('edit');
    setIsDrawerOpen(true);
    setIsAddingCampaigns(false);
    setAddCampaignsSearchQuery('');
  };

  const handleSaveChanges = async () => {
    if (
      !customerId ||
      !activeGroupId ||
      !editGroupName.trim() ||
      editGroupCampaigns.length === 0 ||
      updateCampaignGroup.isLoading
    ) {
      return;
    }

    const campaignIds = editGroupCampaigns
      .map((campaign) => Number(campaign.id))
      .filter((id) => Number.isFinite(id));

    if (campaignIds.length === 0) {
      showError(
        'Unable to update group',
        'Select at least one valid campaign before saving changes.',
      );
      return;
    }

    try {
      const updatedGroup = await updateCampaignGroup.mutateAsync({
        groupId: activeGroupId,
        groupName: editGroupName.trim(),
        campaignIds,
      });

      if (updatedGroup) {
        setEditGroupName(updatedGroup.groupName);
        setEditGroupCampaigns(
          updatedGroup.campaignList
            .map((campaign) => mapApiCampaignToUI(campaign))
            .filter((campaign): campaign is Campaign => campaign !== null),
        );
        setActiveGroupId(updatedGroup.id);
      }

      setDrawerMode('view');
      setIsAddingCampaigns(false);
      showSuccess('Campaign group updated', 'Changes have been saved.');
    } catch (error) {
      showError(
        'Unable to update group',
        'Something went wrong while saving the changes.',
      );
    }
  };

  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!customerId || !activeGroupId || deleteCampaignGroup.isLoading) {
      return;
    }

    try {
      const deleted = await deleteCampaignGroup.mutateAsync({
        groupId: activeGroupId,
      });

      if (deleted) {
        showSuccess('Campaign group deleted', 'The group has been removed.');
      }
      setIsDeleteDialogOpen(false);
      handleCloseDrawer();
    } catch (error) {
      showError(
        'Unable to delete group',
        'Something went wrong while deleting the campaign group.',
      );
      setIsDeleteDialogOpen(false);
    }
  };

  const handleRemoveCampaign = (campaignId: string) => {
    setEditGroupCampaigns((prev) =>
      prev.filter((campaign) => campaign.id !== campaignId),
    );
  };

  const handleAddCampaigns = () => {
    setIsAddingCampaigns(true);
    setAddCampaignsSearchQuery('');
  };

  const handleToggleCampaignToAdd = (campaignId: string) => {
    setEditGroupCampaigns((prev) => {
      const isAlreadySelected = prev.some(
        (campaign) => campaign.id === campaignId,
      );

      if (isAlreadySelected) {
        return prev.filter((campaign) => campaign.id !== campaignId);
      }

      const campaignToAdd =
        allCampaigns.find((campaign) => campaign.id === campaignId) ||
        activeGroup?.campaignList.find(
          (campaign) => campaign.id === campaignId,
        ) ||
        null;

      if (!campaignToAdd) {
        return prev;
      }

      return [...prev, campaignToAdd];
    });
  };

  const handleToggleCampaign = (campaignId: string) => {
    setSelectedCampaigns((prev) => {
      const next = new Set(prev);
      if (next.has(campaignId)) {
        next.delete(campaignId);
      } else {
        next.add(campaignId);
      }
      return next;
    });
  };

  return (
    <>
      <Box
        bg="surface.panel"
        borderRadius="16px"
        border="1px solid"
        borderColor="surface.border"
        w="100%"
        p={6}>
        <Flex
          justify="space-between"
          align={{ base: 'flex-start', md: 'center' }}
          mb={6}
          direction={{ base: 'column', md: 'row' }}
          gap={{ base: 3, md: 0 }}>
          <Text
            fontFamily="marketingPage"
            fontWeight="bold"
            fontSize="lg"
            color="content.title">
            CAMPAIGN GROUPS
          </Text>
          <Button
            bg="icon.green"
            color="white"
            size="md"
            px={4}
            py={2}
            borderRadius="md"
            fontWeight="600"
            buttonName="add-new-group"
            fontFamily="marketingPage"
            onClick={handleOpenCreateDrawer}
            _hover={{ bg: 'green.600' }}
            maxW={{ base: '100%', md: 'auto' }}
            overflow="hidden">
            <Icon
              iconName="plus"
              boxSize={4}
              mr={2}
              color="white"
              flexShrink={0}
            />
            <Text isTruncated>Add New Group</Text>
          </Button>
        </Flex>

        {isCampaignGroupsError ? (
          <Center py={16}>
            <Text color="red.500">
              We were unable to load campaign groups. Please try again later.
            </Text>
          </Center>
        ) : isCampaignGroupsLoading ? (
          <Center py={16}>
            <Spinner color="icon.green" size="lg" />
          </Center>
        ) : tableGroups.length === 0 ? (
          <Center py={16}>
            <Text color="content.muted">
              No campaign groups yet. Create one to get started.
            </Text>
          </Center>
        ) : (
          <CampaignGroupsTable
            groups={tableGroups}
            onGroupClick={handleGroupClick}
            onManageClick={handleManageClick}
          />
        )}
      </Box>

      <ViewGroupDrawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        group={activeGroup}
        mode={drawerMode}
        onEdit={handleEditGroupFromView}
        onSave={handleSaveChanges}
        onDelete={handleDeleteClick}
        editGroupName={editGroupName}
        onEditGroupNameChange={setEditGroupName}
        editGroupCampaigns={editGroupCampaigns}
        onRemoveCampaign={handleRemoveCampaign}
        onAddCampaigns={handleAddCampaigns}
        isAddingCampaigns={isAddingCampaigns}
        allCampaigns={allCampaigns}
        onToggleCampaignToAdd={handleToggleCampaignToAdd}
        addCampaignsSearchQuery={addCampaignsSearchQuery}
        onAddCampaignsSearchChange={setAddCampaignsSearchQuery}
      />

      <DeleteGroupDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        groupName={activeGroup?.groupName ?? ''}
        isLoading={deleteCampaignGroup.isLoading}
      />

      <CreateGroupDrawer
        isOpen={isCreateDrawerOpen}
        onClose={handleCloseCreateDrawer}
        groupName={groupName}
        onGroupNameChange={setGroupName}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        selectedCampaigns={selectedCampaigns}
        onToggleCampaign={handleToggleCampaign}
        allCampaigns={allCampaigns}
        onCreate={handleCreateGroup}
      />

      <CustomToast
        isOpen={toastState.isOpen}
        title={toastState.title}
        description={toastState.description}
        type={toastState.type}
        onClose={hideToast}
        position={{ top: '90px', right: '20px' }}
      />
    </>
  );
};
